import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { vaiTroRepository } from '../../../lib/repositories/vai-tro.repository';
import { useToast } from '../../../lib/toast';
import type { UserRolesCoPhienBan } from '../types';
import { KHOA_VAI_TRO, thuLai, useDanhSachVaiTro } from './useVaiTro';

/**
 * Vai trò lớp 2 hiện có của một người, kèm `ETag`. Luôn đọc lại khi mở ngăn
 * (`staleTime: 0`): `ETag` cũ gần như chắc chắn thành `412` lúc lưu.
 */
export function useVaiTroCuaNguoi(userId: string | null) {
  return useQuery<UserRolesCoPhienBan, ApiError>({
    queryKey: KHOA_VAI_TRO.cuaNguoi(userId ?? ''),
    queryFn: () => vaiTroRepository.vaiTroCuaNguoi(userId ?? ''),
    enabled: Boolean(userId),
    staleTime: 0,
    gcTime: 0,
    retry: thuLai,
  });
}

/** Vai trò lớp 2 của một dòng trong bảng người dùng. */
export interface VaiTroCuaDong {
  dangTai: boolean;
  loi: boolean;
  /** Tên vai trò, theo thứ tự danh sách của tổ chức; vai trò vừa bị xoá thì bỏ. */
  ten: string[];
}

/**
 * Vai trò lớp 2 của nhiều người cùng lúc, cho cột "Vai trò" của bảng người
 * dùng. Danh sách người dùng không mang vai trò lớp 2, nên đọc từng người
 * (`GET /users/{id}/roles`) — tổ chức khoảng 30 người (ADR-0013). Chung khoá
 * cache với ngăn Gán vai trò: lưu ở ngăn là ô trong bảng đổi theo.
 */
export function useVaiTroCuaNhieuNguoi(userIds: string[]): Map<string, VaiTroCuaDong> {
  const dsVaiTro = useDanhSachVaiTro();
  const ketQua = useQueries({
    queries: userIds.map((id) => ({
      queryKey: KHOA_VAI_TRO.cuaNguoi(id),
      queryFn: () => vaiTroRepository.vaiTroCuaNguoi(id),
      staleTime: 30_000,
      retry: thuLai,
    })),
  });
  const thuTu = dsVaiTro.data ?? [];
  const ra = new Map<string, VaiTroCuaDong>();
  userIds.forEach((id, i) => {
    const q = ketQua[i];
    const coIds = new Set(q?.data?.role_ids ?? []);
    ra.set(id, {
      dangTai: Boolean(q?.isLoading) || dsVaiTro.isLoading,
      loi: Boolean(q?.error && !q.data) || Boolean(dsVaiTro.error && !dsVaiTro.data),
      ten: thuTu.filter((r) => coIds.has(r.id)).map((r) => r.name),
    });
  });
  return ra;
}

/**
 * Lưu tập vai trò với `If-Match`. Thành công thì ghi kết quả (cùng `ETag` mới)
 * vào cache, và tải lại danh sách vai trò vì `user_count` đã đổi.
 *
 * `412 ROLE_VERSION_CONFLICT`: người khác vừa đổi — tải lại vai trò của người
 * này để ngăn dựng lại theo bản mới; câu báo do ngăn tự hiện. `422
 * ROLE_NOT_FOUND`: có vai trò vừa bị xoá — tải lại danh sách vai trò.
 */
export function useGanVaiTroNguoiDung() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<
    UserRolesCoPhienBan,
    ApiError,
    { userId: string; ten: string; roleIds: string[]; etag: string }
  >({
    mutationFn: ({ userId, roleIds, etag }) => vaiTroRepository.ganVaiTroChoNguoi(userId, roleIds, etag),
    onSuccess: (kq, { ten }) => {
      queryClient.setQueryData(KHOA_VAI_TRO.cuaNguoi(kq.user_id), kq);
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
      toast.success(t('vaiTroLop2.daGanVaiTro', { ten }));
    },
    onError: (err, { userId }) => {
      if (err.status === 412) {
        void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.cuaNguoi(userId) });
      }
      if (err.status === 422) void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
    },
  });
}
